/**
 * @file Month and year scaffold derivation for the Recalls board.
 */

import {
    addRecallMonths,
    getRecallMonthKey,
    isValidRecallMonthKey,
} from "./recallsMonthQueries.js";

function getYearKey(monthKey) {
    return isValidRecallMonthKey(monthKey) ? monthKey.slice(0, 4) : "";
}

function compareMonthKeys(a, b) {
    return a.localeCompare(b);
}

function getMonthRange(startMonth, endMonth) {
    if (!isValidRecallMonthKey(startMonth) || !isValidRecallMonthKey(endMonth)) {
        return [];
    }
    if (startMonth > endMonth) {return [];}

    const months = [];
    let cursor = startMonth;
    while (cursor <= endMonth) {
        months.push(cursor);
        cursor = addRecallMonths(cursor, 1);
    }
    return months;
}

function getLatestMonth(months) {
    return months
        .filter(isValidRecallMonthKey)
        .sort(compareMonthKeys)
        .at(-1) || null;
}

function getOldestMonth(months) {
    return months
        .filter(isValidRecallMonthKey)
        .sort(compareMonthKeys)[0] || null;
}

function getDefaultExpandedYears({
    activeMonths,
    currentMonth,
    monthKeys,
    nextMonth,
    showPreviousMonths,
}) {
    const years = new Set([getYearKey(currentMonth)]);
    years.add(getYearKey(nextMonth));
    activeMonths
        .filter((monthKey) => monthKey <= nextMonth)
        .map(getYearKey)
        .forEach((year) => {
            if (year) {years.add(year);}
        });
    if (showPreviousMonths === true) {
        monthKeys
            .filter((monthKey) => monthKey < currentMonth)
            .map(getYearKey)
            .forEach((year) => {
                if (year) {years.add(year);}
            });
    }
    return years;
}

/**
 * Builds visible Recalls month buckets grouped by year.
 * @param {object} options Bucket options.
 * @param {Array<string>} [options.activeMonthKeys=[]] Loaded active row months.
 * @param {Array<string>} [options.closedMonthKeys=[]] Loaded closed row months.
 * @param {string|null} [options.latestBoundary=null] Latest known month.
 * @param {string|null} [options.oldestBoundary=null] Oldest known month.
 * @param {Array<string>} [options.loadedMonthKeys=[]] Explicitly loaded months.
 * @param {Array<string>} [options.expandedYearHeadings=[]] User-expanded years.
 * @param {Array<string>} [options.collapsedYearHeadings=[]] User-collapsed years.
 * @param {boolean} [options.showClosed=false] Whether closed rows are visible.
 * @param {boolean} [options.showPreviousMonths=false] Whether previous months show.
 * @returns {{monthKeys: Array<string>, years: Array<object>}} Month model.
 */
export function buildRecallsMonthBucketModel({
    activeMonthKeys = [],
    closedMonthKeys = [],
    collapsedYearHeadings = [],
    expandedYearHeadings = [],
    latestBoundary = null,
    loadedMonthKeys = [],
    oldestBoundary = null,
    showClosed = false,
    showPreviousMonths = false,
} = {}) {
    const currentMonth = getRecallMonthKey();
    const nextMonth = addRecallMonths(currentMonth, 1);
    const visibleMonths = new Set([currentMonth, nextMonth]);
    const activeMonths = activeMonthKeys.filter(isValidRecallMonthKey);
    const closedMonths = closedMonthKeys.filter(isValidRecallMonthKey);
    const loadedMonths = loadedMonthKeys.filter(isValidRecallMonthKey);

    activeMonths.forEach((monthKey) => visibleMonths.add(monthKey));
    loadedMonths.forEach((monthKey) => visibleMonths.add(monthKey));
    if (showClosed === true) {
        closedMonths
            .filter((monthKey) =>
                visibleMonths.has(monthKey) || showPreviousMonths === true)
            .forEach((monthKey) => visibleMonths.add(monthKey));
    }

    const futureEnd = getLatestMonth([
        latestBoundary,
        nextMonth,
        ...activeMonths,
        ...loadedMonths,
    ]) || nextMonth;
    getMonthRange(currentMonth, futureEnd)
        .forEach((monthKey) => visibleMonths.add(monthKey));

    if (showPreviousMonths === true) {
        const previousEnd = addRecallMonths(currentMonth, -1);
        const previousStart = getOldestMonth([
            oldestBoundary,
            ...activeMonths,
            ...closedMonths,
            ...loadedMonths,
        ]);
        getMonthRange(previousStart, previousEnd)
            .forEach((monthKey) => visibleMonths.add(monthKey));
    }

    const monthKeys = Array.from(visibleMonths)
        .filter(isValidRecallMonthKey)
        .sort(compareMonthKeys);
    const collapsedYears = new Set(collapsedYearHeadings.map(String));
    const expandedYears = new Set(expandedYearHeadings.map(String));
    const defaultExpandedYears = getDefaultExpandedYears({
        activeMonths,
        currentMonth,
        monthKeys,
        nextMonth,
        showPreviousMonths,
    });
    const yearMap = new Map();

    monthKeys.forEach((monthKey) => {
        const year = getYearKey(monthKey);
        if (!yearMap.has(year)) {
            yearMap.set(year, {
                expanded: expandedYears.has(year) ||
                    defaultExpandedYears.has(year) && !collapsedYears.has(year),
                months: [],
                year,
            });
        }
        yearMap.get(year).months.push(monthKey);
    });

    return {
        monthKeys,
        years: Array.from(yearMap.values())
            .sort((a, b) => a.year.localeCompare(b.year)),
    };
}
